/**
 * Utility functions for the InputEditor component
 *
 * This file contains all the helper functions for manipulating the Slate editor:
 * - Text formatting (bold, italic)
 * - Block formatting (alignment, lists, headers)
 * - Indentation management
 * - Link operations
 */

import { Editor, Transforms, Element as SlateElement, Range } from 'slate';
import {
  CustomElement,
  CustomElementFormat,
  AlignType,
  ListType,
  LIST_TYPES,
  TEXT_ALIGN_TYPES,
} from './input-editor.types';

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if a format string is a valid alignment type
 */
export const isAlignType = (format: string): format is AlignType => {
  return TEXT_ALIGN_TYPES.includes(format as AlignType);
};

/**
 * Check if a format string is a valid list type
 */
export const isListType = (format: string): format is ListType => {
  return LIST_TYPES.includes(format as ListType);
};

// ============================================================================
// TEXT MARK OPERATIONS (Bold, Italic, etc.)
// ============================================================================

/**
 * Check if a text mark (bold, italic) is currently active at the selection
 */
export const isMarkActive = (editor: Editor, format: string): boolean => {
  const marks = Editor.marks(editor);
  return marks ? marks[format as keyof typeof marks] === true : false;
};

/**
 * Toggle a text mark (bold, italic) at the current selection
 */
export const toggleMark = (editor: Editor, format: string): void => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// ============================================================================
// BLOCK ELEMENT OPERATIONS (Alignment, Lists, Headers)
// ============================================================================

/**
 * Check if a block format is currently active at the selection
 *
 * @param editor - The Slate editor instance
 * @param format - The block format to check
 * @param blockType - Whether to check 'type' or 'align' property
 */
export const isBlockActive = (
  editor: Editor,
  format: string,
  blockType: 'type' | 'align' = 'type',
): boolean => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          if (blockType === 'align' && 'align' in n) {
            return n.align === format;
          }
          return n.type === format;
        }
        return false;
      },
    }),
  );

  return !!match;
};

/**
 * Toggle a block format (alignment, list, header) at the current selection
 *
 * This function handles:
 * - Text alignment (left, center, right, justify)
 * - List formatting (bulleted, numbered)
 * - Header formatting
 * - Paragraph formatting
 */
export const toggleBlock = (
  editor: Editor,
  format: CustomElementFormat,
): void => {
  const isActive = isBlockActive(
    editor,
    format,
    isAlignType(format) ? 'align' : 'type',
  );
  const isList = isListType(format);

  // First, unwrap any existing list containers if we're not setting alignment
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      isListType(n.type) &&
      !isAlignType(format),
    split: true,
  });

  // Set the new properties based on format type
  let newProperties: Partial<SlateElement>;
  if (isAlignType(format)) {
    // For alignment, toggle the align property
    newProperties = { align: isActive ? undefined : format };
  } else {
    // For other formats, set the element type
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }

  Transforms.setNodes<SlateElement>(editor, newProperties);

  // If we're creating a list, wrap it in the appropriate list container
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

// ============================================================================
// INDENTATION OPERATIONS
// ============================================================================

/**
 * Check if the current selection can be indented further
 * Maximum indentation level is 10
 */
export const canIndent = (editor: Editor): boolean => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n.type === 'paragraph' || n.type === 'heading'),
    }),
  );

  return !!match && (match[0] as CustomElement).indent !== undefined
    ? (match[0] as CustomElement).indent! < 10
    : true;
};

/**
 * Check if the current selection can be outdented
 * Can only outdent if there's existing indentation
 */
export const canOutdent = (editor: Editor): boolean => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n.type === 'paragraph' || n.type === 'heading'),
    }),
  );

  return !!match && (match[0] as CustomElement).indent !== undefined
    ? (match[0] as CustomElement).indent! > 0
    : false;
};

/**
 * Increase indentation level for the current selection
 * Only works on paragraphs and headings, with max level of 10
 */
export const increaseIndent = (editor: Editor): void => {
  if (!canIndent(editor)) return;

  // Get current nodes and calculate new indent
  const nodes = Array.from(
    Editor.nodes(editor, {
      at: editor.selection!,
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n.type === 'paragraph' || n.type === 'heading'),
    }),
  );

  if (nodes.length > 0) {
    const [node] = nodes[0];
    const currentIndent = (node as CustomElement).indent || 0;
    const newIndent = Math.min(currentIndent + 1, 10);

    Transforms.setNodes<CustomElement>(
      editor,
      { indent: newIndent },
      {
        at: editor.selection!,
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          (n.type === 'paragraph' || n.type === 'heading'),
      },
    );
  }
};

/**
 * Decrease indentation level for the current selection
 * Removes indentation property when reaching level 0
 */
export const decreaseIndent = (editor: Editor): void => {
  if (!canOutdent(editor)) return;

  // Get current nodes and calculate new indent
  const nodes = Array.from(
    Editor.nodes(editor, {
      at: editor.selection!,
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n.type === 'paragraph' || n.type === 'heading'),
    }),
  );

  if (nodes.length > 0) {
    const [node] = nodes[0];
    const currentIndent = (node as CustomElement).indent || 0;
    const newIndent = Math.max(currentIndent - 1, 0);

    Transforms.setNodes<CustomElement>(
      editor,
      newIndent === 0 ? { indent: undefined } : { indent: newIndent },
      {
        at: editor.selection!,
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          (n.type === 'paragraph' || n.type === 'heading'),
      },
    );
  }
};

// ============================================================================
// LINK OPERATIONS
// ============================================================================

/**
 * Check if there's currently a link at the selection
 */
export const isLinkActive = (editor: Editor): boolean => {
  const [link] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
  return !!link;
};

/**
 * Remove link formatting from the current selection
 */
export const unwrapLink = (editor: Editor): void => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

/**
 * Wrap the current selection in a link element
 *
 * @param editor - The Slate editor instance
 * @param url - The URL for the link
 */
export const wrapLink = (editor: Editor, url: string): void => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);

  const link: CustomElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

/**
 * Insert or update a link at the current selection
 *
 * @param editor - The Slate editor instance
 * @param url - The URL for the link
 */
export const insertLink = (editor: Editor, url: string): void => {
  if (url && url.trim()) {
    wrapLink(editor, url.trim());
  }
};
